import React, { useState, useEffect } from 'react';
import { X, Upload, Image as ImageIcon, Star } from 'lucide-react';
import { getImageUrl } from '../utils/imageUtils';

const ImageUpload = ({ images = [], onImagesChange, onImageDelete, maxImages = 10 }) => {
    const mapImagesToPreviews = (items) => {
        if (!items || !Array.isArray(items)) return [];
        return items.map((img, index) => {
            if (img && typeof img === 'object' && (img.file || img.existing !== undefined)) {
                return img;
            }
            const isObj = img && typeof img === 'object';
            return {
                url: isObj ? (img.url || '') : img,
                isPrimary: isObj ? (Boolean(img.isPrimary) || index === 0) : index === 0,
                file: null,
                existing: true,
                id: isObj ? (img._id || img.id) : null,
                _id: isObj ? (img._id || img.id) : null
            };
        });
    };

    const [previews, setPreviews] = useState(() => mapImagesToPreviews(images));
    const [dragActive, setDragActive] = useState(false);

    // Sync previews when images prop changes externally
    useEffect(() => {
        setPreviews(mapImagesToPreviews(images));
    }, [images]);

    const handleFiles = (files) => {
        const fileArray = Array.from(files);

        // Check if adding these files would exceed the limit
        if (previews.length + fileArray.length > maxImages) {
            alert(`Maximum ${maxImages} images allowed. You can add ${maxImages - previews.length} more.`);
            return;
        }

        // Create preview URLs for new files
        const newPreviews = fileArray.map((file, index) => ({
            url: URL.createObjectURL(file),
            isPrimary: previews.length === 0 && index === 0, // First image is primary if no images exist
            file: file,
            existing: false
        }));

        const updatedPreviews = [...previews, ...newPreviews];
        setPreviews(updatedPreviews);

        // Pass files to parent component
        if (onImagesChange) {
            onImagesChange(updatedPreviews);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFiles(e.dataTransfer.files);
        }
    };

    const handleChange = (e) => {
        e.preventDefault();
        if (e.target.files && e.target.files.length > 0) {
            handleFiles(e.target.files);
        }
    };

    const removeImage = (index) => {
        const removedItem = previews[index];
        const updatedPreviews = previews.filter((_, i) => i !== index);

        // If removed image was primary and there are other images, make first image primary
        if (removedItem?.isPrimary && updatedPreviews.length > 0) {
            updatedPreviews[0].isPrimary = true;
        }

        setPreviews(updatedPreviews);

        if (onImagesChange) {
            onImagesChange(updatedPreviews);
        }

        if (onImageDelete && removedItem) {
            onImageDelete(removedItem);
        }
    };

    const setPrimaryImage = (index) => {
        const updatedPreviews = previews.map((preview, i) => ({
            ...preview,
            isPrimary: i === index
        }));

        setPreviews(updatedPreviews);

        if (onImagesChange) {
            onImagesChange(updatedPreviews);
        }
    };

    return (
        <div className="space-y-4">
            {/* Upload Area */}
            {previews.length < maxImages && (
                <div
                    className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${dragActive
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                        }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                >
                    <input
                        type="file"
                        id="image-upload"
                        multiple
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="space-y-2">
                        <Upload className="mx-auto h-10 w-10 text-gray-400" />
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            <label htmlFor="image-upload" className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 cursor-pointer">
                                Click to upload
                            </label>
                            {' '}or drag and drop
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            PNG, JPG, WebP up to 5MB (Max {maxImages} images)
                        </p>
                        <p className="text-xs font-semibold text-primary-600 dark:text-primary-400">
                            {previews.length}/{maxImages} images attached
                        </p>
                    </div>
                </div>
            )}

            {/* Image Previews */}
            {previews.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {previews.map((preview, index) => (
                        <div
                            key={index}
                            className={`relative group rounded-xl overflow-hidden border-2 shadow-sm transition-all ${preview.isPrimary
                                    ? 'border-yellow-400 dark:border-yellow-500 ring-2 ring-yellow-400/30'
                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                                }`}
                        >
                            {/* Image */}
                            <div className="aspect-square bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden">
                                <img
                                    src={preview.file ? preview.url : getImageUrl(preview.url)}
                                    alt={`Preview ${index + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400';
                                    }}
                                />
                            </div>

                            {/* Overlay with actions */}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center gap-2">
                                {/* Set as Primary button */}
                                {!preview.isPrimary && (
                                    <button
                                        type="button"
                                        onClick={() => setPrimaryImage(index)}
                                        className="bg-yellow-500 hover:bg-yellow-600 text-white p-2 rounded-full shadow-lg transition-transform hover:scale-110"
                                        title="Set as primary image"
                                    >
                                        <Star className="h-4 w-4" />
                                    </button>
                                )}

                                {/* Remove button */}
                                <button
                                    type="button"
                                    onClick={() => removeImage(index)}
                                    className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-transform hover:scale-110"
                                    title="Remove image"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>

                            {/* Primary badge */}
                            {preview.isPrimary && (
                                <div className="absolute top-2 left-2 bg-yellow-500 text-white text-[11px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-md">
                                    <Star className="h-3 w-3 fill-current" />
                                    Primary
                                </div>
                            )}

                            {/* Status badge: Existing or New */}
                            <div className="absolute top-2 right-2">
                                {preview.existing ? (
                                    <span className="bg-blue-600/90 backdrop-blur-xs text-white text-[10px] font-semibold px-1.5 py-0.5 rounded shadow">
                                        Attached
                                    </span>
                                ) : (
                                    <span className="bg-emerald-600/90 backdrop-blur-xs text-white text-[10px] font-semibold px-1.5 py-0.5 rounded shadow">
                                        New
                                    </span>
                                )}
                            </div>

                            {/* Image number */}
                            <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded backdrop-blur-xs">
                                {index + 1}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Help text */}
            {previews.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    <Star className="inline h-3.5 w-3.5 text-yellow-500 fill-current shrink-0" />
                    <span>The primary image will be displayed as the main cover photo. Hover to set a new primary or delete photos.</span>
                </p>
            )}
        </div>
    );
};

export default ImageUpload;
